// Virtual filesystem over a stack of mounted .pk3 archives. Like the Quake 3
// filesystem, archives are searched in reverse mount order and are mounted in
// alphabetical order, so later pk3s override files from earlier ones.

import { Pk3Archive } from './pk3';

export class VirtualFS {
  private archives: Pk3Archive[] = [];

  get mounted(): readonly Pk3Archive[] {
    return this.archives;
  }

  async mountBlob(blob: Blob, name: string): Promise<Pk3Archive> {
    const archive = await Pk3Archive.open(blob, name);
    this.archives = this.archives.filter((a) => a.name.toLowerCase() !== name.toLowerCase());
    this.archives.push(archive);
    this.archives.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    return archive;
  }

  /** A filesystem of just these (already opened) archives, e.g. the bundle of one map. */
  static of(archives: Pk3Archive[]): VirtualFS {
    const vfs = new VirtualFS();
    vfs.archives = [...archives].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    return vfs;
  }

  /** The archive that provides a file. */
  archiveOf(path: string): Pk3Archive | undefined {
    return this.find(path);
  }

  unmount(name: string): void {
    this.archives = this.archives.filter((a) => a.name !== name);
  }

  private find(path: string): Pk3Archive | undefined {
    const key = normalizePath(path);
    for (let i = this.archives.length - 1; i >= 0; i--) {
      if (this.archives[i].has(key)) return this.archives[i];
    }
    return undefined;
  }

  has(path: string): boolean {
    return this.find(path) !== undefined;
  }

  async read(path: string): Promise<Uint8Array | null> {
    const archive = this.find(path);
    return archive ? archive.read(normalizePath(path)) : null;
  }

  async readText(path: string): Promise<string | null> {
    const data = await this.read(path);
    return data ? new TextDecoder('latin1').decode(data) : null;
  }

  /** Lower-case paths of all files under dir (non-recursive unless recursive) with the given extension. */
  list(dir: string, ext: string, recursive = false): string[] {
    const prefix = normalizePath(dir).replace(/\/?$/, '/');
    const out = new Set<string>();
    for (const a of this.archives) {
      for (const key of a.entries.keys()) {
        if (!key.startsWith(prefix) || !key.endsWith(ext)) continue;
        if (!recursive && key.indexOf('/', prefix.length) !== -1) continue;
        out.add(key);
      }
    }
    return [...out].sort();
  }

  /** Which archive provides a file (for showing map sources in the UI). */
  sourceOf(path: string): string | undefined {
    return this.find(path)?.name;
  }
}

export function normalizePath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\/+/, '').toLowerCase();
}
